import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip } from "@amzn/stencil-react-components/chip";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, Flex } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Select } from "@amzn/stencil-react-components/select";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil";
import Input from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import { SearchField } from "@amzn/uxm-stencil-react-components/lib/src/stencil/search-field";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { ListType } from "common/context/lists";
import { useLists } from "common/context/lists/ListsContext";
import { calculateItemsDiff } from "common/helpers/lists-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useSearchExperience } from "hooks";
import { debounce } from "lib/helpers";
import { Visibility } from "lib/models/list-models";
import { ItemModel } from "lib/services/catalogical-models";
import { CatalogicalSearchRequestContent, SearchExperienceDomains } from "lib/services/shop-search-service";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";

interface ListModalProps {
  onClose: () => void;
  initialData?: {
    name?: string;
    description?: string;
    visibility?: Visibility;
    allowedDomains?: string[];
    items?: ItemModel[];
    ownershipBindle?: string;
    sharedWithBindle?: string;
  };
  listId?: string;
  isEdit?: boolean;
}

const ITEM_DISPLAY_FIELD: Record<string, keyof ItemModel> = {
  aiforme: "title",
  software: "name",
};

const getListType = (visibility: Visibility, ownershipBindle?: string): ListType => {
  if (visibility === Visibility.PRIVATE) {
    return ListType.YOUR_LISTS;
  }

  if (visibility === Visibility.PUBLIC) {
    return ListType.YOUR_LISTS;
  }

  if (visibility === Visibility.SHARED) {
    return ownershipBindle ? ListType.SHARED_WITH_YOU_LISTS : ListType.SHARED_LISTS;
  }

  throw new Error(`Unsupported visibility: ${visibility}`);
};

const visibilityOptions = [
  { label: "Private", value: "private" },
  { label: "Shared", value: "shared" },
];

const ListModal: FunctionComponent<ListModalProps> = ({ onClose, initialData, listId, isEdit = false }) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [visibility, setVisibility] = useState<Visibility>(initialData?.visibility || Visibility.PRIVATE);
  const [showBindleOptions, setShowBindleOptions] = useState<boolean>(false);
  const [ownershipBindle, setOwnershipBindle] = useState<string | undefined>(initialData?.ownershipBindle || undefined);
  const [sharedWithBindle, setSharedWithBindle] = useState<string | undefined>(
    initialData?.sharedWithBindle || undefined
  );
  const getDomainFromUrl = () => {
    return window.location.href.includes("aiforme") ? "aiforme" : "software";
  };

  const domains = initialData?.allowedDomains || [getDomainFromUrl()];
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [query, setQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<ItemModel[]>(initialData?.items || []);
  const [showDropdown, setShowDropdown] = useState(true);

  const { createList, updateList, getYourLists, addItemToList, removeItemFromList } = useLists();
  const { addMessageBanner } = useMessageBannersBox();
  const { items, isLoading, searchCatalogItems } = useSearchExperience<ItemModel>(
    domains[0] as SearchExperienceDomains | "aiforme"
  );

  const getSearchResultsDebounced = useMemo(
    () =>
      debounce(async (searchText: string) => {
        const nextRequest: CatalogicalSearchRequestContent = {
          searchQuery: searchText,
          useSemanticSearch: false,
          limit: 5,
          offset: 0,
          vectorField: "embeddings",
        };
        searchCatalogItems(nextRequest);
        return [];
      }, 1000)[0],
    []
  );

  useEffect(() => {
    if (query && query != "") getSearchResultsDebounced(query);
  }, [query]);

  useEffect(() => {
    const shouldShowBindle = visibility === Visibility.SHARED;
    setShowBindleOptions(shouldShowBindle);
    if (!shouldShowBindle) {
      setOwnershipBindle("");
      setSharedWithBindle("");
    }
  }, [visibility]);

  const handleSubmit = async () => {
    setIsProcessing(true);
    const trimmedSharedBindle = sharedWithBindle?.trim();
    const trimmedOwnershipBindle = ownershipBindle?.trim();
    const data = {
      name,
      description,
      visibility,
      allowedDomains: domains,
      sharedWithBindle: trimmedSharedBindle || undefined,
      ownershipBindle: trimmedOwnershipBindle || undefined,
      itemIds: selectedItems.map((item) => item.taxonomyId),
    };

    const itemsDiff = calculateItemsDiff(selectedItems, initialData?.items);
    const listType = getListType(visibility, trimmedOwnershipBindle);

    try {
      if (isEdit && listId) {
        await updateList(listId, data, listType);
        if (itemsDiff.added.length > 0) {
          await addItemToList(
            itemsDiff.added.map((item) => item.taxonomyId),
            { listId },
            listType
          );
        }
        if (itemsDiff.removed.length > 0) {
          await Promise.all(itemsDiff.removed.map((item) => removeItemFromList(listId, item.taxonomyId, listType)));
        }
        addMessageBanner(getString("lists.modal.updateSuccess"), MessageBannerType.Success, 5000);
      } else {
        await createList(data, listType);
        addMessageBanner(getString("lists.modal.createSuccess"), MessageBannerType.Success, 5000);
      }
      getYourLists({
        includeItemDetails: true,
        allowedDomains: domains,
      });
    } catch (error) {
      addMessageBanner(
        getString(isEdit ? "lists.modal.updateError" : "lists.modal.createError"),
        MessageBannerType.Error
      );
    } finally {
      setIsProcessing(false);
      onClose();
    }
  };

  const selectItem = (result: ItemModel) => {
    const alreadySelected = selectedItems.some((item) => item.taxonomyId === result.taxonomyId);
    if (!alreadySelected) {
      setSelectedItems((prev) => [...prev, result]);
    }
    setShowDropdown(false);
  };

  const removeItem = (result: ItemModel) => {
    setSelectedItems((prev) => prev.filter((item) => item.taxonomyId !== result.taxonomyId));
  };

  const renderResultsText = (result: ItemModel) => {
    const field = ITEM_DISPLAY_FIELD[domains[0]] || "name";
    return result[field] as string;
  };

  const isValid = name.trim().length > 0;

  return (
    <ModalContent
      titleText={getString(isEdit ? "lists.modal.editTitle" : "lists.modal.title")}
      maxWidth="800px"
      buttons={[
        <Button key="cancel" touchpoint="list-modal-cancel" variant={ButtonVariant.Tertiary} onClick={onClose}>
          {getString("lists.modal.cancel")}
        </Button>,
        <Button
          key="submit"
          touchpoint="list-modal-submit"
          variant={ButtonVariant.Primary}
          disabled={!isValid || isProcessing}
          onClick={handleSubmit}
          data-cy="list-submit-button"
        >
          {getString(isEdit ? "lists.modal.update" : "lists.modal.create")}
        </Button>,
      ]}
    >
      <Col gridGap="S300" width={"500px"}>
        <InputWrapper id="list-name" labelText={getString("lists.modal.name")} required={true}>
          {(inputProps) => (
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={getString("lists.modal.namePlaceholder")}
              touchpoint="list-name-input"
              data-cy="list-name-input"
              {...inputProps}
            />
          )}
        </InputWrapper>

        <InputWrapper id="list-search" labelText={getString("lists.modal.search")}>
          {(inputProps) => (
            <>
              <SearchField
                touchpoint="search-input"
                data-cy="search-input"
                data-testid="search-field"
                query={query}
                onChange={(q) => {
                  setQuery(q);
                  setShowDropdown(true);
                }}
                renderResult={showDropdown ? renderResultsText : undefined}
                results={showDropdown ? items || [] : []}
                onResultClick={(result) => {
                  selectItem(result);
                  setQuery("");
                }}
                shouldShowListOnNoResults={showDropdown}
                isLoading={showDropdown ? isLoading : false}
                {...inputProps}
              />
              <Flex flexDirection="row" gridGap={"S200"} flexWrap="wrap">
                {selectedItems.map((item) => (
                  <Chip key={item.taxonomyId} onDismiss={() => removeItem(item)}>
                    {renderResultsText(item)}
                  </Chip>
                ))}
              </Flex>
            </>
          )}
        </InputWrapper>

        <InputWrapper id="list-description" labelText={getString("lists.modal.description")}>
          {(inputProps) => (
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={getString("lists.modal.descriptionPlaceholder")}
              rows={3}
              touchpoint="list-description-textarea"
              data-cy="list-description-textarea"
              {...inputProps}
            />
          )}
        </InputWrapper>

        <InputWrapper id="list-visibility" labelText={getString("lists.modal.visibility")}>
          {(inputProps) => (
            <Select
              value={visibilityOptions.find((opt) => opt.value === visibility)}
              onChange={(option) => setVisibility(option.value as Visibility)}
              options={visibilityOptions}
              valueAccessor="value"
              renderOption={(option) => option.label}
              renderSelectedValue={(option) => option.label}
              data-cy="list-visibility-select"
              {...inputProps}
            />
          )}
        </InputWrapper>

        {showBindleOptions && (
          <>
            <InputWrapper
              id="list-shared-bindle"
              labelText={getString("lists.modal.sharedBindle")}
              footer={getString("lists.modal.bindleDescription")}
            >
              {(inputProps) => (
                <Input
                  value={sharedWithBindle}
                  onChange={(e) => setSharedWithBindle(e.target.value)}
                  placeholder={getString("lists.modal.sharedBindlePlaceholder")}
                  touchpoint="list-sharedBindle-input"
                  data-cy="list-shared-bindle-input"
                  {...inputProps}
                />
              )}
            </InputWrapper>

            {/* Uncomment when adding ownership bindle functionality */}
            {/* <InputWrapper 
              id="list-ownership-bindle" 
              labelText={getString("lists.modal.ownershipBindle")} 
              footer={getString("lists.modal.bindleDescription")}
            >
              {(inputProps) => (
                <Input
                  value={ownershipBindle}
                  onChange={(e) => setOwnershipBindle(e.target.value)}
                  placeholder={getString("lists.modal.ownershipBindlePlaceholder")}
                  touchpoint="list-ownershipBindle-input"
                  {...inputProps}
                />
              )}
            </InputWrapper> */}
          </>
        )}
      </Col>
    </ModalContent>
  );
};

export default ListModal;
